import React, { useState, useEffect } from "react";
import {
  Clock,
  BookOpen,
  CheckCircle,
  TrendingUp,
  BarChart3,
  PieChart as PieIcon,
  AlertCircle,
  Plus,
  ArrowRight,
  Target,
  Award,
} from "lucide-react";
import TimeBar from "./TimeBar";
import FrequentlyAdressed from "./FrequentlyAdressed";
import MoreRecent from "./MoreRecent";
import SubmittedProblemsStats from "./SubmittedProblemsStats";
import {
  getStudentDashboardStats,
  StudentDashboardStatsOutput,
} from "../../../server/Student-dashboard/requests";
import { Link } from "react-router-dom";

// Carte statistique optimisée
interface StatCardProps {
  title: string;
  value: string | number;
  color: string;
  icon: React.ElementType;
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  color,
  icon: Icon,
  subtitle,
}) => (
  <div
    className={`flex flex-col bg-white rounded-xl shadow-sm p-3 sm:p-4 lg:p-6 transition-all duration-300 border-l-4 ${color} hover:shadow-md h-24 sm:h-28 lg:h-32`}
  >
    <div className="flex justify-between items-start mb-1 sm:mb-2">
      <div className="text-gray-500 text-xs sm:text-sm font-medium leading-tight flex-1 pr-2">
        {title}
      </div>
      <Icon className="text-gray-400 flex-shrink-0" size={14} />
    </div>
    <div className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 leading-none mb-1">
      {value}
    </div>
    {subtitle && (
      <div className="text-xs sm:text-sm text-gray-500 leading-tight">
        {subtitle}
      </div>
    )}
  </div>
);

// Interface pour nouveaux utilisateurs
const NewUserWelcome: React.FC = () => (
  <div className="w-full min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
    <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-8 sm:py-12 lg:py-16">
      {/* Header d'accueil */}
      <div className="text-center mb-8 sm:mb-12">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-600 rounded-full mb-6">
          <Award className="w-10 h-10 text-white" />
        </div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
          Bienvenue sur Objectif Révision !
        </h1>
        <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
          Vous êtes maintenant prêt à commencer votre parcours vers l'excellence
          en mathématiques. Créez votre première soumission pour commencer à
          progresser.
        </p>
      </div>

      {/* Cartes d'action */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 sm:mb-12">
        {/* Première soumission */}
        <div className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-all duration-300 border border-gray-100">
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
              <Plus className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Première soumission
            </h3>
          </div>
          <p className="text-gray-600 mb-4">
            Commencez par soumettre votre premier problème. Nos enseignants
            qualifiés vous aideront à le résoudre.
          </p>
          <Link
            to="/submissions"
            className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium transition-colors"
          >
            Commencer maintenant
            <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </div>

        {/* Découvrir la plateforme */}
        <div className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-all duration-300 border border-gray-100">
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
              <Target className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Découvrir la plateforme
            </h3>
          </div>
          <p className="text-gray-600 mb-4">
            Explorez les différentes fonctionnalités et types de soumissions
            disponibles.
          </p>
          <Link
            to="/submissions"
            className="inline-flex items-center text-green-600 hover:text-green-700 font-medium transition-colors"
          >
            Explorer
            <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </div>
      </div>

      {/* Section statistiques vides avec message */}
      <div className="bg-white rounded-xl shadow-sm p-6 sm:p-8 border border-gray-100">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 text-center">
          Vos statistiques apparaîtront ici
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl sm:text-3xl font-bold text-gray-400 mb-2">
              0
            </div>
            <div className="text-sm text-gray-500">Total soumissions</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl sm:text-3xl font-bold text-gray-400 mb-2">
              0
            </div>
            <div className="text-sm text-gray-500">En attente</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl sm:text-3xl font-bold text-gray-400 mb-2">
              0
            </div>
            <div className="text-sm text-gray-500">Résolus</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl sm:text-3xl font-bold text-gray-400 mb-2">
              0%
            </div>
            <div className="text-sm text-gray-500">Taux de réussite</div>
          </div>
        </div>
        <div className="text-center mt-6">
          <p className="text-gray-500 text-sm">
            Commencez par créer votre première soumission pour voir vos
            statistiques évoluer !
          </p>
        </div>
      </div>
    </div>
  </div>
);

const Dashboard: React.FC = () => {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();
  const monthName = new Date(0, currentMonth - 1).toLocaleString("fr-FR", {
    month: "long",
  });

  // États pour les données dynamiques
  const [stats, setStats] = useState<StudentDashboardStatsOutput | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Récupération des données dynamiques
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await getStudentDashboardStats();

        if (response.status === 200 && response.body) {
          setStats(response.body as StudentDashboardStatsOutput);
        } else {
          throw new Error("Erreur lors de la récupération des statistiques");
        }
      } catch (err) {
        console.error("Erreur lors de la récupération des statistiques:", err);
        setError("Impossible de charger les statistiques");
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  // Calculs pour les statistiques (avec données dynamiques ou fallback)
  const totalSubmissions = stats?.totalCount || 0;
  const resolvedProblems = stats?.statusCount?.resolved || 0;
  const pendingProblems =
    (stats?.statusCount?.in_standby || 0) +
    (stats?.statusCount?.in_processing || 0);
  const progressPercentage =
    totalSubmissions > 0
      ? Math.round((resolvedProblems / totalSubmissions) * 100)
      : 0;

  // Vérifier si l'utilisateur est nouveau (pas de données)
  const isNewUser = !loading && !error && totalSubmissions === 0;

  // Afficher l'interface d'accueil pour les nouveaux utilisateurs
  if (isNewUser) {
    return <NewUserWelcome />;
  }

  return (
    <div className="w-full min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8 space-y-4 sm:space-y-6 lg:space-y-8">
        {/* Header optimisé */}
        <div className="text-center sm:text-left">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-1 sm:mb-2 leading-tight">
            Tableau de Bord
          </h1>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
            <span className="text-xs sm:text-sm lg:text-base text-gray-600">
              {monthName.charAt(0).toUpperCase() + monthName.slice(1)}{" "}
              {currentYear}
            </span>
            <div className="flex items-center justify-center sm:justify-end space-x-1 sm:space-x-2 text-xs sm:text-sm text-gray-500">
              <Clock size={12} className="flex-shrink-0" />
              <span className="truncate">
                Dernière mise à jour:{" "}
                {currentDate.toLocaleTimeString("fr-FR", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
          </div>
        </div>

        {/* Cartes statistiques avec données dynamiques */}
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="flex flex-col bg-white rounded-xl shadow-sm p-3 sm:p-4 lg:p-6 border-l-4 border-gray-300 h-24 sm:h-28 lg:h-32">
                  <div className="flex justify-between items-start mb-1 sm:mb-2">
                    <div className="h-3 bg-gray-200 rounded w-20" />
                    <div className="w-4 h-4 bg-gray-200 rounded" />
                  </div>
                  <div className="h-6 sm:h-7 lg:h-8 bg-gray-200 rounded w-12 mb-1" />
                  <div className="h-2 bg-gray-200 rounded w-24" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
              <span className="text-red-800 text-sm">{error}</span>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4">
            <StatCard
              title="Total soumissions"
              value={totalSubmissions}
              color="border-blue-500"
              icon={BookOpen}
              subtitle="Tous types confondus"
            />
            <StatCard
              title="En attente"
              value={pendingProblems}
              color="border-yellow-500"
              icon={AlertCircle}
              subtitle="En cours de traitement"
            />
            <StatCard
              title="Résolus"
              value={resolvedProblems}
              color="border-green-500"
              icon={CheckCircle}
              subtitle="Problèmes traités"
            />
            <StatCard
              title="Taux de résolution"
              value={`${progressPercentage}%`}
              color="border-purple-500"
              icon={TrendingUp}
              subtitle="Problèmes résolus"
            />
          </div>
        )}

        {/* Grille principale optimisée */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
          {/* TimeBar - non modifié */}
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 transition-all duration-300 hover:shadow-md border border-gray-100">
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h2 className="text-base sm:text-lg font-semibold text-gray-800 leading-tight">
                Temps d'étude détaillé
              </h2>
              <BarChart3 size={18} className="text-gray-400 flex-shrink-0" />
            </div>
            <TimeBar />
          </div>

          {/* Problèmes récents - même taille que TimeBar */}
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 transition-all duration-300 hover:shadow-md border border-gray-100">
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h2 className="text-base sm:text-lg font-semibold text-gray-800 leading-tight">
                Dernières soumissions
              </h2>
              <BookOpen size={18} className="text-gray-400 flex-shrink-0" />
            </div>
            <div className="overflow-hidden">
              <MoreRecent />
            </div>
          </div>
        </div>

        {/* Sujets fréquemment abordés - COMMENTÉ */}
        {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
          <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 lg:p-6 transition-all duration-300 hover:shadow-md border border-gray-100 h-96 sm:h-[420px] lg:h-[450px]">
            <div className="flex justify-between items-center mb-3 sm:mb-4 lg:mb-6">
              <h2 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-800 leading-tight">
                Sujets abordés
              </h2>
              <PieIcon size={16} className="text-gray-400 flex-shrink-0" />
            </div>
            <div className="h-[calc(100%-60px)] overflow-hidden">
              <FrequentlyAdressed />
            </div>
          </div>
        </div> */}

        {/* Section inférieure optimisée - COMMENTÉ */}
        {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
          <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 lg:p-6 transition-all duration-300 hover:shadow-md border border-gray-100 h-80 sm:h-[350px] lg:h-[380px]">
            <div className="flex justify-between items-center mb-3 sm:mb-4 lg:mb-6">
              <h2 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-800 leading-tight">
                Problèmes récents
              </h2>
              <BookOpen size={16} className="text-gray-400 flex-shrink-0" />
            </div>
            <div className="h-[calc(100%-60px)] overflow-hidden">
              <MoreRecent />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 lg:p-6 transition-all duration-300 hover:shadow-md border border-gray-100 h-80 sm:h-[350px] lg:h-[380px]">
            <div className="flex justify-between items-center mb-3 sm:mb-4 lg:mb-6">
              <h2 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-800 leading-tight">
                Statistiques détaillées
              </h2>
              <CheckCircle size={16} className="text-gray-400 flex-shrink-0" />
            </div>
            <div className="h-[calc(100%-60px)] overflow-hidden">
              <SubmittedProblemsStats />
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default Dashboard;
